import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMPageWrap } from "../../components/Simple/CRMPageWrap/CRMPageWrap";
import { CRMLegalMenuBarContainer } from "../../containers/CRMLegalMenuBarContainer/CRMLegalMenuBarContainer";
import { CRMHomesMenuBarContainer } from "../../containers/CRMHomesMenuBarContainer/CRMHomesMenuBarContainer";
import { CRMProbateMenuBarContainer } from "../../containers/CRMProbateMenuBarContainer/CRMProbateMenuBarContainer";
import { Background } from "../../components/BuildingBlocks/Background";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMPadding } from "../../components/Simple/CRMPadding/CRMPadding";
import { CRMSpacingColumn } from "../../components/CRMSpacingColumn/CRMSpacingColumn";
import NotFoundPage from "../NotFoundPage/NotFoundPage";
import { findCategory, findProduct, TGuideProductId } from "../../guides/manifest";
import { generatedGuides, TGeneratedGuide } from "../../guides/manifest.generated";
import { isGuidesAccessAllowed } from "../CRMGuidesPage/guidesAccess";
import { CRMGuidesHeader, TCRMGuidesHeaderCrumb } from "../../components/CRM/CRMGuidesHeader/CRMGuidesHeader";
import { CRMGuidesMarkAsRead } from "../../components/CRM/CRMGuidesMarkAsRead/CRMGuidesMarkAsRead";
import { fetchMyGuideReads } from "../../components/CRM/CRMGuidesMarkAsRead/guideReadsApi";
import { TGuideRead } from "../../../../domain/codecs/GuideRead";

const MenuBarForProduct = (props: { productId: TGuideProductId; containerProps: TContainerStateProps }): JSX.Element => {
    switch (props.productId) {
        case "legal":
            return <CRMLegalMenuBarContainer {...props.containerProps} />;
        case "homes":
            return <CRMHomesMenuBarContainer {...props.containerProps} />;
        case "probate":
            return <CRMProbateMenuBarContainer {...props.containerProps} />;
    }
};

const GuideBody = (props: { guide: TGeneratedGuide }): JSX.Element => (
    <article className="crm-guide-page__body">
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            skipHtml={true}
        >
            {props.guide.body_markdown}
        </ReactMarkdown>
    </article>
);

const CRMGuidePage = (props: TContainerStateProps): JSX.Element => {
    const [reads, setReads] = React.useState<Array<TGuideRead>>([]);

    React.useEffect(() => {
        let cancelled = false;
        fetchMyGuideReads()
            .then((rows) => { if (!cancelled) setReads(rows); })
            .catch((e) => { console.error("[guides] failed to load my reads — guide will render as never-read", e); });
        return () => { cancelled = true; };
    }, []);

    if (!isGuidesAccessAllowed(props.state.global.user.user_role)) {
        return <NotFoundPage state={props.state} dispatch={props.dispatch} />;
    }

    const productId = props.state.routes.params.productId;
    const slug = props.state.routes.params.slug;
    const product = productId ? findProduct(productId) : undefined;
    const guide = product && slug
        ? generatedGuides.find((g) => g.product === product.id && g.slug === slug)
        : undefined;

    if (!product || !guide) {
        return <NotFoundPage state={props.state} dispatch={props.dispatch} />;
    }

    const category = findCategory(product, guide.category);

    const handleRecorded = (row: TGuideRead): void => {
        setReads((prev) => [row, ...prev]);
    };

    const breadcrumbs: Array<TCRMGuidesHeaderCrumb> = [
        { label: "Guides", href: "/crm/guides" },
        { label: product.title, href: `/crm/guides/${product.id}` },
        { label: category ? category.title : guide.category },
        { label: guide.title },
    ];

    return (
        <CRMPageWrap>
            <MenuBarForProduct productId={product.id} containerProps={props} />
            <Background padding={`${CRMSpacing.X_LARGE} 0px`}>
                <CRMPadding size="large">
                    <CRMSpacingColumn spacing="medium">
                        <CRMGuidesHeader
                            title={guide.title}
                            breadcrumbs={breadcrumbs}
                            backHref={`/crm/guides/${product.id}`}
                            backLabel={`${product.title} guides`}
                        />
                        <GuideBody guide={guide} />
                        <CRMGuidesMarkAsRead
                            product={guide.product}
                            slug={guide.slug}
                            contentHash={guide.content_hash}
                            reads={reads}
                            onRecorded={handleRecorded}
                        />
                    </CRMSpacingColumn>
                </CRMPadding>
            </Background>
        </CRMPageWrap>
    );
};

export default CRMGuidePage;
